import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { UserState } from "@/context/User/UserReducer";
import { updateReporter } from "@/services/Reporter.service";
import { Reporter, UpdateReporterParams } from "@/types/reporter.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";

const formSchema = z.object({
	reporterid: z.number(),
	reportername: z.string(),
	designationid: z.number(),
});

const EditReporter: React.FC<{
	user: UserState | null;
	reporter: Reporter;
}> = ({ user, reporter }) => {
	const [open, setOpen] = useState(false);

	const queryClient = useQueryClient();

	const editReporter = useMutation({
		mutationKey: ["reporter_update", reporter.reporterid],
		mutationFn: (data: UpdateReporterParams) =>
			updateReporter(user?.accessToken!, data),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["reporter_" + reporter.reporterid],
				exact: true,
				refetchType: "all",
			});
			setOpen(false);
			form.reset();
			toast.success("Success", {
				description: `Reporter ${variables.reportername} has been successfully created`,
			});
			editReporter.reset();
		},
		onError: (err) => {
			toast.error("Error!", { description: "Error adding reporter, try again" });
			console.log(err);
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			reporterid: reporter.reporterid,
			reportername: reporter.reportername,
			designationid: reporter.designationid,
		},
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		console.log(data);
		editReporter.mutate(data);
	};

	const disabled = () => {
		if (editReporter.isPending) return true;
		if (editReporter.isSuccess) return true;
		return false;
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="default">Edit Reporter</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="text-left font-bold text-2xl">
						Edit Reporter
					</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<FormField
							name="reporterid"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Reporter ID</FormLabel>
									<FormControl>
										<Input
											type="number"
											defaultValue={reporter.reporterid}
											onChange={(e) => field.onChange(e.target.valueAsNumber)}
											disabled={disabled()}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							name="reportername"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Name of Reporter</FormLabel>
									<FormControl>
										<Input type="text" {...field} disabled={disabled()} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							name="designationid"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Designation ID</FormLabel>
									<FormControl>
										<Input
											type="number"
											defaultValue={reporter.designationid}
											onChange={(e) => field.onChange(e.target.valueAsNumber)}
											disabled={disabled()}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							className="mt-4 w-full"
							disabled={editReporter.isPending || editReporter.isSuccess}
						>
							{editReporter.isPending ? "Updating..." : "Edit Reporter"}
						</Button>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default EditReporter;
